import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { usePermission, useToast, useUpdateRoleMutation } from "@/hooks";
import { Button, Label, Input } from "@/components/ui";
import { RoleValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { Role } from "@/types";

import { AlertDialogDelete } from "../../dialogs";

type FormGroupProps = {
  handleClose: () => void;
  role: Role;
};

type RoleRequestDTO = {
  id: string;
  name: string;
};

export function FormUpdateRole({ handleClose, role }: FormGroupProps) {
  const { t } = useTranslation();

  const { checkPermission } = usePermission();

  const { toast } = useToast();

  const initialValues = {
    id: role.id,
    name: role.name || "",
  };

  const { mutateAsync, isLoading } = useUpdateRoleMutation();
  const { handleSubmit, getFieldProps, errors, touched, resetForm } = useFormik(
    {
      initialValues,
      validationSchema: RoleValidationSchema(),
      onSubmit: async (values) => {
        handleCreateRoleSubmit(values);
      },
    }
  );

  async function handleCreateRoleSubmit(request: RoleRequestDTO) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
          resetForm();
          handleClose();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="space-y-2">
        <div className="flex flex-col gap-2">
          <Label>{t("LABEL.name")}</Label>
          <Input
            isInvalid={touched.name && errors.name}
            isValid={touched.name && !errors.name}
            {...getFieldProps("name")}
            messageError={errors.name}
          />
        </div>
      </div>
      <div className="w-full space-y-2">
        <Button isLoading={isLoading} className="w-full" disabled={!checkPermission("positions", "edit positions")}>
          {t("LABEL.save")}
        </Button>
        <AlertDialogDelete
          endpoint={`/positions/${role.id}`}
          queryKey="positions"
          callBack={handleClose}
        >
          <Button
            type="button"
            className="w-full bg-destructive hover:bg-destructive/80"
            disabled={!checkPermission("positions", "delete positions")}
          >
            {t("LABEL.delete")}
          </Button>
        </AlertDialogDelete>
      </div>
    </form>
  );
}
